import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Plus, Search, Trash2 } from 'lucide-react'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/data-table'
import { StatusBadge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { toast } from 'sonner'
import { useState } from 'react'

interface TenantRow {
  id: string
  name: string
  slug: string
  plan: string
  status: string
  max_m365_tenants: number
  max_users: number
  m365_count: number
  user_count: number
  created_at: string
}

export function AdminClientsPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({
    name: '', adminEmail: '', adminName: '', adminPassword: '',
    plan: 'starter', maxM365Tenants: '5', maxUsers: '500',
  })

  const { data: response, isLoading } = useQuery({
    queryKey: ['admin', 'tenants', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), perPage: '20' })
      if (search) params.set('search', search)
      return apiFetchJson<{
        ok: boolean
        data: TenantRow[]
        meta: { page: number; totalPages: number; total: number }
      }>(`/admin/tenants?${params}`)
    },
  })

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/tenants', {
        name: form.name,
        adminEmail: form.adminEmail,
        adminName: form.adminName,
        adminPassword: form.adminPassword,
        plan: form.plan,
        maxM365Tenants: parseInt(form.maxM365Tenants),
        maxUsers: parseInt(form.maxUsers),
      }),
    onSuccess: () => {
      toast.success('Cliente creado correctamente')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] })
      setShowCreate(false)
      setForm({ name: '', adminEmail: '', adminName: '', adminPassword: '', plan: 'starter', maxM365Tenants: '5', maxUsers: '500' })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/tenants/${id}`),
    onSuccess: () => {
      toast.success('Cliente eliminado y todos sus datos borrados')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const handleDelete = (row: TenantRow) => {
    const typed = window.prompt(
      `ATENCION: esto eliminara definitivamente "${row.name}" y TODOS sus datos (tenants M365, usuarios, licencias, recomendaciones, snapshots, auditoria). Esta accion es irreversible.\n\nPara confirmar, escribe el nombre exacto del cliente:`,
    )
    if (typed === null) return
    if (typed !== row.name) {
      toast.error('El nombre no coincide. Eliminacion cancelada.')
      return
    }
    deleteMutation.mutate(row.id)
  }

  const update = (field: string, value: string) => setForm((f) => ({ ...f, [field]: value }))

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gestion de Clientes"
        description="Alta, edicion y gestion de clientes de la plataforma"
        actions={
          <Button onClick={() => setShowCreate(!showCreate)}>
            <Plus className="h-4 w-4" /> Nuevo cliente
          </Button>
        }
      />

      {/* Create form */}
      {showCreate && (
        <Card>
          <h3 className="mb-4 text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Nuevo cliente
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Nombre de la organizacion *
              </label>
              <input type="text" value={form.name} onChange={(e) => update('name', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}
                placeholder="Empresa S.L." />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Plan</label>
              <select value={form.plan} onChange={(e) => update('plan', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}>
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Nombre del administrador *
              </label>
              <input type="text" value={form.adminName} onChange={(e) => update('adminName', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}
                placeholder="Juan Garcia" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Email del administrador *
              </label>
              <input type="email" value={form.adminEmail} onChange={(e) => update('adminEmail', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}
                placeholder="admin@empresa.com" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Contrasena del administrador *
              </label>
              <input type="password" value={form.adminPassword} onChange={(e) => update('adminPassword', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }}
                placeholder="Min. 8 caracteres" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Max tenants M365</label>
                <input type="number" value={form.maxM365Tenants} onChange={(e) => update('maxM365Tenants', e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Max usuarios</label>
                <input type="number" value={form.maxUsers} onChange={(e) => update('maxUsers', e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-border)' }} />
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending}>
              Crear cliente
            </Button>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancelar</Button>
          </div>
        </Card>
      )}

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1" style={{ maxWidth: '350px' }}>
          <Search className="absolute left-3 top-2.5 h-4 w-4" style={{ color: 'var(--color-text-tertiary)' }} />
          <input type="text" placeholder="Buscar cliente..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="w-full rounded-md border py-2 pl-9 pr-3 text-sm" style={{ borderColor: 'var(--color-border)' }} />
        </div>
      </div>

      {/* Table */}
      <Card padding={false}>
        <div className="p-6">
          {!isLoading && !response?.data?.length ? (
            <EmptyState icon={Building2} title="Sin clientes" description="Crea tu primer cliente con el boton de arriba" />
          ) : (
            <DataTable
              columns={[
                { key: 'name', header: 'Nombre' },
                { key: 'slug', header: 'Slug', className: 'hidden md:table-cell' },
                { key: 'plan', header: 'Plan', render: (r) => <StatusBadge status={(r as Record<string, string>).plan} /> },
                { key: 'status', header: 'Estado', render: (r) => <StatusBadge status={(r as Record<string, string>).status} /> },
                { key: 'm365_count', header: 'M365', render: (r) => <span className="tabular-nums">{(r as Record<string, number>).m365_count}</span> },
                { key: 'user_count', header: 'Usuarios', render: (r) => <span className="tabular-nums">{(r as Record<string, number>).user_count}</span> },
                {
                  key: 'created_at', header: 'Creado',
                  render: (r) => formatDate((r as Record<string, string>).created_at),
                },
                {
                  key: 'actions', header: '',
                  render: (r) => {
                    const row = r as unknown as TenantRow
                    return (
                      <div className="flex justify-end gap-2">
                        <Button variant="primary" size="sm" onClick={() => window.location.href = `/admin/clients/${row.id}`}>
                          Gestionar
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(row)}
                          loading={deleteMutation.isPending && deleteMutation.variables === row.id}
                          title="Eliminar cliente y todos sus datos"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  },
                },
              ]}
              data={(response?.data ?? []) as unknown as Record<string, unknown>[]}
              loading={isLoading}
              page={response?.meta?.page ?? 1}
              totalPages={response?.meta?.totalPages ?? 1}
              total={response?.meta?.total}
              onPageChange={setPage}
            />
          )}
        </div>
      </Card>
    </div>
  )
}
